import React, { useState, useRef, useEffect } from 'react';
import ProgressiveImage from './components/ProgressiveImage';
import { Link } from 'react-router-dom';
import { ReviewCards } from './components/ReviewCards';
import { reviews } from './data/reviews';

const APP_STORE_URL = 'https://apps.apple.com/us/app/househunt-fun-with-real-homes/id6737416185';

const App: React.FC = () => {
  const getButtonRef = useRef<HTMLAnchorElement>(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (getButtonRef.current) {
        const rect = getButtonRef.current.getBoundingClientRect();
        setShowStickyHeader(rect.top < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleIconClick = () => {
    if (!isSpinning) {
      setIsSpinning(true);
      setTimeout(() => setIsSpinning(false), 500);
    }
  };

  useEffect(() => {
    if (document.readyState === 'complete') {
      setIsLoading(false);
    } else {
      const handleLoad = () => {
        setIsLoading(false);
      };
      window.addEventListener('load', handleLoad);

      const timeoutId = setTimeout(() => {
        setIsLoading(false);
      }, 2000);

      return () => {
        window.removeEventListener('load', handleLoad);
        clearTimeout(timeoutId);
      };
    }
  }, []);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <img
          src={`${process.env.PUBLIC_URL}/images/features/app-icon.png`}
          alt="Loading..."
          className="loading-icon"
        />
      </div>
    );
  }

  return (
    <div className="app">
      <div className={`sticky-header ${showStickyHeader ? 'visible' : ''}`}>
        <div className="sticky-header-content">
          <div className="sticky-left">
            <span>Househunt</span>
          </div>
          <div className="sticky-center">
            <img
              src={`${process.env.PUBLIC_URL}/images/features/app-icon.png`}
              alt="Househunt"
              className="sticky-app-icon"
            />
          </div>
          <div className="sticky-right">
            <a
              className="get-button"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Get
            </a>
          </div>
        </div>
      </div>
      {/* Header Section */}
      <header className="header">
        <div className="header-content">
          <div className="app-info">
            <img
              src={`${process.env.PUBLIC_URL}/images/features/app-icon.png`}
              className={`app-icon ${isSpinning ? 'spinning' : ''}`}
              onClick={handleIconClick}
              alt="Househunt"
            />
            <div className="app-details">
              <h1>Househunt</h1>
              <p className="subtitle">Monopoly with real houses.</p>
            </div>
          </div>
          <div className="signup-container">
            <div className="get-button-container">
              <a
                ref={getButtonRef}
                className="get-button"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get
              </a>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="stats">
          <div className="stat-item">
            <span className="stat-header">4687 RATINGS</span>
            <span className="rating">4.7</span>
            <img
              src={`${process.env.PUBLIC_URL}/images/features/stars-count.png`}
              alt="4.7 out of 5 stars"
              className="stars-rating"
              loading="lazy"
            />
          </div>
          <div className="stat-item">
            <span className="stat-header">CATEGORY</span>
            <img
              src={`${process.env.PUBLIC_URL}/images/features/simulation-icon.png`}
              alt=""
              className="category-icon"
              loading="lazy"
            />
            <span className="stat-label">Simulation</span>
          </div>
          <div className="stat-item">
            <span className="stat-header">LIVE IN</span>
            <div className="live-in-flags">
              <img src={`${process.env.PUBLIC_URL}/images/features/us-icon.png`} alt="" className="location-icon" />
              <img src={`${process.env.PUBLIC_URL}/images/features/canada-icon.png`} alt="" className="location-icon" />
            </div>
            <span className="stat-label">US & Canada</span>
          </div>
          <div className="stat-item">
            <span className="stat-header">DEVELOPER</span>
            <img src={`${process.env.PUBLIC_URL}/images/features/developer-icon.png`} alt="" className="developer-icon" />
            <span className="stat-label">Closing Theory Studios, Inc.</span>
          </div>
        </div>
      </header>

      {/* Features Grid */}
      <main className="features">
        <div className="features-grid">
          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/guess-prices.png`}
              alt="Guess house prices"
              priority={true}
            />
          </div>

          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/buy-houses.png`}
              alt="Buy houses"
              priority={true}
            />
          </div>

          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/renovate.png`}
              alt="Renovate"
            />
          </div>

          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/passive-income.png`}
              alt="Passive income"
            />
          </div>

          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/sell-houses.png`}
              alt="Sell houses"
            />
          </div>

          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/unlock-city.png`}
              alt="Unlock city"
            />
          </div>
        </div>
      </main>

      {/* Reviews Section */}
      <section className="reviews">
        <h2>Ratings & Reviews</h2>
        <div className="rating-summary">
          <div className="review-container review-container-left">
            <div className="rating-big">
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <span className="number">4.7</span>
                <span className="out-of">out of 5</span>
              </div>
              <div className="total-ratings">4687 Ratings</div>
            </div>
          </div>
          <div className="review-container review-container-right">
            <div className="rating-bars">
              <div className="rating-bar-row">
                <div className="stars-rating-small">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/features/stars-5.png`}
                    alt="5 stars"
                    loading="lazy"
                  />
                </div>
                <div className="rating-bar">
                  <div className="bar-fill" style={{ width: '69%' }}></div>
                </div>
              </div>
              <div className="rating-bar-row">
                <div className="stars-rating-small">
                  <img src={`${process.env.PUBLIC_URL}/images/features/stars-4.png`} alt="4 stars" />
                </div>
                <div className="rating-bar">
                  <div className="bar-fill" style={{ width: '21%' }}></div>
                </div>
              </div>
              <div className="rating-bar-row">
                <div className="stars-rating-small">
                  <img src={`${process.env.PUBLIC_URL}/images/features/stars-3.png`} alt="3 stars" />
                </div>
                <div className="rating-bar">
                  <div className="bar-fill" style={{ width: '6%' }}></div>
                </div>
              </div>
              <div className="rating-bar-row">
                <div className="stars-rating-small">
                  <img src={`${process.env.PUBLIC_URL}/images/features/stars-2.png`} alt="2 stars" />
                </div>
                <div className="rating-bar">
                  <div className="bar-fill" style={{ width: '1%' }}></div>
                </div>
              </div>
              <div className="rating-bar-row">
                <div className="stars-rating-small">
                  <img src={`${process.env.PUBLIC_URL}/images/features/stars-1.png`} alt="1 star" />
                </div>
                <div className="rating-bar">
                  <div className="bar-fill" style={{ width: '3%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <h3 className="helpful-reviews-header">Most helpful reviews</h3>
        <div className="reviews-scroll">
          {[...reviews]
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="review-title">{review.title}</div>
                  <div className="review-meta">
                    <img src={`${process.env.PUBLIC_URL}/images/features/stars-${review.rating}.png`} alt={`${review.rating} stars`} className="review-stars" />
                    <span className="review-date">{new Date(review.date).toLocaleDateString()}</span>
                    <span className="review-author">{review.author}</span>
                  </div>
                </div>
                <div className="review-body">
                  {review.content}
                </div>
              </div>
            ))}
        </div>
      </section>

      <footer className="footer">
        <div className="footer-links">
          <Link to="/support">Support</Link>
        </div>
      </footer>
    </div>
  );
};

export default App;
